import { nomSalle, ordreSalle } from '../supervision/vues/salles.js'

/**
 * CAMÉRA D'UNE PORTE — les calculs purs (24/10/2026).
 *
 * La fiche porte relie une porte UniFi Access à la caméra du registre de Sonde
 * qui la filme. Gestion ne garde que l'uid (« cam-013 ») ; le nom, la salle et
 * l'état se lisent chez Sonde, par le relais existant des vues de supervision
 * (droit sonde.read). Aucune image ni vidéo n'entre dans Gestion.
 *
 * Rien ici ne touche React ni le réseau : le tri ROOM 1, ROOM 2, ROOM 3, les
 * indices et la caméra liée devenue inconnue se testent sans navigateur
 * (tests/porte-camera.test.ts). Seul import : les noms de salle.
 */

/** Les types de poste de Sonde qui sont des caméras (les NVR, viewports et flux n'en sont pas). */
export const KINDS_CAMERA = ['camera_unifi', 'camera_hikvision', 'camera_wifi'] as const

/** La lecture des caméras en service, par le relais (`/api/v1/supervision` + ce chemin). */
export const CHEMIN_CAMERAS_SONDE = `/v/devices?kind=${KINDS_CAMERA.join(',')}&limit=500`

/** Motif d'uid du registre de Sonde (route /infrastructure/appareils/:uid, relais, table). */
export const UID_CAMERA = /^[a-z0-9][a-z0-9-]{1,62}$/

export type EtatCamera = 'ONLINE' | 'DEGRADED' | 'OFFLINE' | 'OFF_EXPECTED' | 'MAINTENANCE' | 'UNKNOWN' | 'STALE' | 'UNAVAILABLE'

/** Ce que la fiche lit d'un poste de la liste de Sonde (forme `deviceBrief`). */
export interface CameraSonde {
  uid: string
  display_name: string
  kind: string
  zone_code: string | null
  state: { display_state: EtatCamera; display_label: string }
}

export interface ListeCameras { items: CameraSonde[]; next_cursor?: string | null }

const objet = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)

const estCamera = (x: unknown): x is CameraSonde => objet(x)
  && typeof x['uid'] === 'string' && typeof x['display_name'] === 'string' && typeof x['kind'] === 'string'
  && (x['zone_code'] === null || x['zone_code'] === undefined || typeof x['zone_code'] === 'string')
  && objet(x['state']) && typeof x['state']['display_state'] === 'string' && typeof x['state']['display_label'] === 'string'

/** La réponse du relais a-t-elle la forme attendue ? Sinon rien n'est affiché plutôt qu'un faux. */
export const estListeCameras = (x: unknown): x is ListeCameras =>
  objet(x) && Array.isArray(x['items']) && (x['items'] as unknown[]).every(estCamera)

/** La pastille d'un état de Sonde. */
export const DOT_ETAT: Readonly<Record<EtatCamera, string>> = {
  ONLINE: 'lc-dot--ok',
  DEGRADED: 'lc-dot--warning',
  MAINTENANCE: 'lc-dot--warning',
  STALE: 'lc-dot--warning',
  OFFLINE: 'lc-dot--error',
  OFF_EXPECTED: 'lc-dot--idle',
  UNKNOWN: 'lc-dot--idle',
  UNAVAILABLE: 'lc-dot--idle',
}

/** Un choix du sélecteur (forme de `ComboItem`, admin/pickers.tsx). */
export interface ChoixCamera { value: string; label: string; hint: string; dot?: string; keywords: string }

/** « ROOM 1 », « Régie ROOM 1 », un autre code de Sonde, ou « Sans salle ». */
export const salleCamera = (zone: string | null | undefined): string => (zone ? nomSalle(zone) : 'Sans salle')

/** ROOM 1, ROOM 2, ROOM 3, puis les autres salles, puis « Sans salle » en dernier. */
const rangSalle = (zone: string | null | undefined): number => (zone ? ordreSalle(zone) : 1000)

const estDeType = (c: CameraSonde): boolean => (KINDS_CAMERA as readonly string[]).includes(c.kind) && UID_CAMERA.test(c.uid)

/** Les caméras de la liste, rangées par salle puis par nom. */
export function camerasRangees(items: readonly CameraSonde[]): CameraSonde[] {
  return items.filter(estDeType).sort((a, b) =>
    rangSalle(a.zone_code) - rangSalle(b.zone_code)
    || salleCamera(a.zone_code).localeCompare(salleCamera(b.zone_code), 'fr', { numeric: true })
    || a.display_name.localeCompare(b.display_name, 'fr', { numeric: true })
    || a.uid.localeCompare(b.uid))
}

/**
 * Pourquoi la liste de Sonde manque : lue en ce moment, lecture échouée, ou
 * jamais lue faute du droit « Consulter la supervision ». L'indice de la
 * caméra liée le dit, au lieu d'annoncer une panne de Sonde qui n'existe pas.
 */
export type ListeAbsente = 'lecture' | 'echec' | 'sans-droit'

export const INDICE_LISTE_ABSENTE: Readonly<Record<ListeAbsente, string>> = {
  lecture: 'Lecture de la liste de Sonde…',
  echec: 'Liste de Sonde indisponible pour l’instant',
  'sans-droit': 'Caméra reliée à cette porte',
}

/**
 * Les choix du sélecteur. `items` vaut null tant que la liste n'est pas lue
 * (lecture en cours, échec, ou pas de sonde.read) : seule la caméra liée
 * reste, pour pouvoir retirer le lien, et `absente` choisit son indice. Une
 * caméra liée absente d'une liste LUE reste proposée en tête : « cam-013 —
 * inconnue de Sonde ».
 */
export function choixCameras(items: readonly CameraSonde[], lie: string | null): ChoixCamera[]
export function choixCameras(items: readonly CameraSonde[] | null, lie: string | null, absente: ListeAbsente): ChoixCamera[]
export function choixCameras(items: readonly CameraSonde[] | null, lie: string | null, absente: ListeAbsente = 'echec'): ChoixCamera[] {
  const choix: ChoixCamera[] = (items ? camerasRangees(items) : []).map((c) => {
    const salle = salleCamera(c.zone_code)
    return {
      value: c.uid, label: c.display_name, hint: `${salle} · ${c.uid}`,
      dot: DOT_ETAT[c.state.display_state] ?? 'lc-dot--idle', keywords: `${c.uid} ${salle}`,
    }
  })
  if (lie !== null && !choix.some((c) => c.value === lie)) {
    choix.unshift(items
      ? { value: lie, label: `${lie} — inconnue de Sonde`, hint: 'Retirée du registre ou identifiant changé', dot: 'lc-dot--idle', keywords: lie }
      : { value: lie, label: lie, hint: INDICE_LISTE_ABSENTE[absente], keywords: lie })
  }
  return choix
}

/** La caméra liée telle que Sonde la décrit, ou null si la liste ne la connaît pas. */
export function cameraDansListe(items: readonly CameraSonde[] | null, uid: string): CameraSonde | null {
  return items?.find((c) => c.uid === uid && estDeType(c)) ?? null
}
